import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getThemeAssets } from '../utils/theme';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const leftSideRef = useRef<HTMLDivElement>(null);

  // Get theme assets from environment
  const themeAssets = getThemeAssets();

  // Mouse tracking for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (leftSideRef.current) {
        const rect = leftSideRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };

    const leftSide = leftSideRef.current;
    if (leftSide) {
      leftSide.addEventListener('mousemove', handleMouseMove);
      return () => leftSide.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/auth/forgot-password?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reset link');
      }

      // Success - show success message
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left Side - Glass Morphism Cards with Background (Bottom on mobile, Left on desktop) */}
      <div ref={leftSideRef} className="w-full h-[100vh] lg:w-1/2 lg:h-screen relative overflow-hidden">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-login-bgLeft"></div>

        {/* Animated Decoration Images */}
        <div
          className="absolute w-24 h-24 transition-transform duration-1000 ease-out opacity-80 hover:opacity-100"
          style={{
            transform: `translate(${mousePosition.x * 60}px, ${mousePosition.y * 40}px) rotate(${mousePosition.x * 10}deg)`,
            top: '10%',
            left: '10%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-16 h-16 transition-transform duration-800 ease-out opacity-60 hover:opacity-90"
          style={{
            transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * 60}px) rotate(${mousePosition.y * 15}deg)`,
            top: '65%',
            right: '15%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>
        <div
          className="absolute w-14 h-14 transition-transform duration-1200 ease-out opacity-50 hover:opacity-80"
          style={{
            transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * -35}px) rotate(${mousePosition.x * -8}deg)`,
            top: '40%',
            left: '75%',
          }}
        >
          <img
            src={themeAssets.loginDecoration}
            alt="Decoration"
            className="w-full h-full object-contain drop-shadow-lg"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-12 py-16">
          {/* Main Heading */}
          <div className="text-center w-full max-w-lg">
            <div className="mb-8 flex justify-center">
              <div className="p-6 bg-white/10 backdrop-blur-lg rounded-full border border-white/20">
                <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-5xl font-bold mb-4 text-white leading-tight">Reset Your Password</h1>
            <p className="text-xl text-blue-100/90 font-light mb-2">
              No worries, we'll help you get back in
            </p>
            <p className="text-base text-blue-200/80">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Forgot Password Form (Top on mobile, Right on desktop) */}
      <div className="w-full lg:w-1/2 lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0">
        <div className="w-full max-w-md mx-auto lg:mx-8">
          <div className="">
            <div className="text-center mb-6">
              <img
                src={themeAssets.loginLogo}
                alt={themeAssets.brandName}
                className="h-10 sm:h-12 mx-auto mb-4"
              />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent mb-2">
                Forgot Password?
              </h1>
              <p className="text-sm sm:text-base text-gray-600">We'll send you a reset link</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {success ? (
              <div className="text-center">
                <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-green-100 rounded-full">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Check Your Email</h3>
                  <p className="text-green-700 text-sm mb-1">
                    We've sent a password reset link to:
                  </p>
                  <p className="text-green-800 font-semibold">{email}</p>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="text-left">
                      <p className="text-sm text-blue-800 font-medium mb-1">What's next?</p>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                        <li>Click the link in the email</li>
                        <li>Create a new password</li>
                        <li>Sign in with your new password</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  className="w-full bg-brandColor text-white py-3 px-4 rounded-lg font-medium hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Send Another Link
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-brandColor text-white py-3 px-4 rounded-lg font-medium hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}

            {/* Back to Sign In */}
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
